"use client";

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import OracleInterpretation from '@/components/tarot/yes-no-page-sections/OracleInterpretation';
import CardSymbolism from '@/components/tarot/yes-no-page-sections/CardSymbolism';
import FAQSection from '@/components/tarot/yes-no-page-sections/FAQSection';
import CardArt from '@/components/tarot/yes-no-page-sections/CardArt';
import YesNoCTA from '@/components/tarot/yes-no-page-sections/YesNoCTA';
import InternalLinks from '@/components/tarot/yes-no-page-sections/InternalLinks';
import { Button } from '@/components/ui/Button';
import { DEFAULT_LOCALE } from '@/config/i18n';

export default function TarotPageClient({ card, relatedCards }) {
  const activeLocale = card.locale ?? DEFAULT_LOCALE;
  const t = useTranslations('tarot.card');

  const heroTitle = card.h1 ?? card.seoTitle ?? card.name;
  const heroSummary = card.metaDescription || card.upright?.meaning?.split('\n\n')[0] || '';

  return (
    <div className="w-full max-w-6xl mx-auto mt-8 p-4 md:p-8 bg-[#100e23]/70 rounded-xl border border-[#6A3683]/40 shadow-xl shadow-[#6A3683]/10">
      <header className="mb-10 text-center space-y-4">
        <p className="text-xs uppercase tracking-[0.35em] text-[#D4B6FF]/80" style={{ fontFamily: 'var(--font-marcellus)' }}>
          {t('hero.badge')}
        </p>
        <h1
          className="text-3xl md:text-4xl lg:text-[2.75rem] leading-tight font-semibold text-white"
          style={{ fontFamily: 'var(--font-marcellus)' }}
        >
          {heroTitle}
        </h1>
        {heroSummary ? (
          <p className="text-sm md:text-base text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {heroSummary}
          </p>
        ) : null}
        <div className="flex items-center justify-center gap-3 my-6 text-sm text-gray-400">
          <span>{t('hero.author.by')}</span>
          <a 
            href={`/${activeLocale}/about-luna`} 
            className="font-semibold text-[#D4B6FF] hover:text-[#A45DE8] hover:underline transition-colors"
          >
            {t('hero.author.name')}
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs text-gray-500">
          <span>
            {t('hero.dates.published')}: <span className="font-semibold text-gray-400">
              {(() => {
                try {
                  if (card.createdAt) {
                    // Dates are normalized to ISO strings from server component
                    const date = new Date(card.createdAt);
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString(activeLocale === 'en' ? 'en-US' : activeLocale === 'es' ? 'es-ES' : 'it-IT', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    }
                  }
                } catch (e) {
                  console.warn('Error formatting published date:', e);
                }
                return 'January 1, 2025';
              })()}
            </span>
          </span>
          {(() => {
            try {
              if (card.updatedAt) {
                // Dates are normalized to ISO strings from server component
                const updatedDate = new Date(card.updatedAt);
                
                // Get createdAt for comparison
                let createdDate = null;
                if (card.createdAt) {
                  createdDate = new Date(card.createdAt);
                }
                
                if (!isNaN(updatedDate.getTime()) && (!createdDate || updatedDate.getTime() !== createdDate.getTime())) {
                  return (
                    <span>
                      {t('hero.dates.lastUpdated')}: <span className="font-semibold text-gray-400">
                        {updatedDate.toLocaleDateString(activeLocale === 'en' ? 'en-US' : activeLocale === 'es' ? 'es-ES' : 'it-IT', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </span>
                  );
                }
              }
            } catch (e) {
              console.warn('Error formatting updated date:', e);
            }
            return null;
          })()}
        </div>
        <div className="flex flex-col items-center gap-3">
          <Link href={`/${activeLocale}#hero-section`}>
            <Button variant="primary" size="lg" className="px-6 py-3">
              {t('hero.cta')}
            </Button>
          </Link>
          <div className="flex items-center gap-4 text-xs text-gray-400 uppercase tracking-widest">
            <span>✧ {t('hero.badgeList.free')}</span>
            <span>✧ {t('hero.badgeList.private')}</span>
          </div>
        </div>
      </header>

      <div className="flex flex-col-reverse md:flex-row md:items-start gap-8">
        <div className="w-full md:w-2/3">
          <OracleInterpretation upright={card.upright} reversed={card.reversed} />
        </div>
        <aside className="w-full md:w-1/3 md:sticky md:top-24 space-y-10">
          <CardSymbolism
            symbolism={card.symbolism}
            variant="sidebar"
            renderArt={() => <CardArt cardName={card.name} imageUrl={card.cardArtUrl} />}
          />
        </aside>
      </div>
      <YesNoCTA />

      <FAQSection faqs={card.faq} />

      <InternalLinks links={card.suggestedInternalLinks} relatedCards={relatedCards} />
    </div>
  );
} 